import {
	filtersEventsChangeKey,
	useFilters
} from '~/composables/useListing/filter';
import { Pagination } from '~/composables/useListing/pagination';
import type { Property as PropertyType } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';
import {
	PropertyFiltersPresenter,
	type PageName
} from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { Property } from './property';

interface UseManagerReturn {
	properties: ComputedRef<PropertyType[]>;
	filters: ComputedRef<PropertyFilters>;
	filteredCount: ComputedRef<number>;
	isFetching: ComputedRef<boolean>;
	isFetchingOnClient: ComputedRef<boolean>;
	isFetchingForTheFirstTime: ComputedRef<boolean>;
	hasProperties: ComputedRef<boolean>;
	hasLessThenTenProperties: ComputedRef<boolean>;
	nextPage: () => void;
	updateFiltersManager: (
		filters: Partial<PropertyFilters>,
		improveChanges?: boolean
	) => void;
	updateFiltersManagerDebounced: (
		filters: Partial<PropertyFilters>,
		improveChanges?: boolean
	) => void;
	clearFilters: () => void;
}

type PerPageOptions = {
	perPageFirstPage?: number;
	perPageNextPages?: number;
};

/**
 * Manages property listing functionality, including filters, pagination, and property data fetching.
 * @returns Functions and reactive data for managing the property listing.
 */
export function useManager(
	page: PageName = 'imoveis',
	collection?: string,
	perPageOptions: PerPageOptions = {},
	initialFilters?: ComputedRef<Partial<PropertyFilters>>,
	config: {
		immediate: boolean;
		shouldScrollToTopOnFiltersChange: boolean;
	} = {
		immediate: import.meta.server,
		shouldScrollToTopOnFiltersChange: true
	}
): UseManagerReturn {
	const route = useRoute();
	const router = useRouter();
	const viewMoreBus = useEventBus<string>('view-more-clicked');

	const {
		filters,
		resetChanges,
		resetFilters,
		clearFilters,
		getCurrentChange,
		updateMultipleFilters,
		updateMultipleFiltersDebounced,
		updatePathFromFilters,
		getStringifiedFilters,
		updateFiltersFromNewPath,
		updateLastRequestChanges,
		setInitialFiltersFromPath,
		filtersHasChangedSinceLastRequest,
		updateFiltersFromLocationSlugFilters
	} = useFilters(page);

	const {
		serverPagination,
		clientPagination,
		displayPagination,
		updateCount,
		nextClientPage,
		nextDisplayPage,
		nextServerPage,
		reset: resetPagination
	} = Pagination();

	const {
		properties,
		addProperties,
		needMoreProperties,
		reset: resetProperties,
		movePropertiesFromCached
	} = Property(
		clientPagination,
		perPageOptions.perPageFirstPage,
		perPageOptions.perPageNextPages
	);

	/**
	 * Initializes filters based on the current URL path if no changes have been made.
	 */
	function setInitialFilters() {
		if (getCurrentChange() === 0) {
			setInitialFiltersFromPath();
		}
	}

	/**
	 * Fetches property data based on current filters and pagination settings.
	 * Resets states if filters or pagination have changed since the last request.
	 * @returns Resolves when data fetching is complete but the returned value is always null to indicate that dont should be used.
	 */
	const propertiesDataFetcher = useAsyncData<null>(
		`properties-listing-${collection ?? page}`,
		async () => {
			try {
				setInitialFilters();

				if (filtersHasChangedSinceLastRequest()) {
					resetPagination();
					resetProperties();
				}

				const [result, error] = await nuxtPropertiesRepository.list({
					page: serverPagination.value.page,
					perPage: serverPagination.value.perPage,
					...initialFilters?.value,
					...filters.value
				});

				if (error) {
					console.error(error);
					return null;
				}

				updateCount(result.pagination.filteredCount);
				addProperties(result.properties);
				updateFiltersFromLocationSlugFilters(result.locationSlugFilters);
				updateLastRequestChanges();

				return null;
			} catch (error) {
				return null;
			}
		},
		{
			immediate: config.immediate
		}
	);

	/*
	 * Computed property returns a boolean value indicating whether the properties are being fetched.
	 */
	const isFetching = computed<boolean>(
		() => propertiesDataFetcher.status.value === 'pending'
	);

	/*
	 * Computed property returns a boolean value indicating whether the properties are being fetched in client side.
	 */
	const isFetchingOnClient = computed<boolean>(
		() => import.meta.client && isFetching.value
	);

	/**
	 * Checks if data is being fetched for the first page.
	 * @returns True if fetching for the first page, otherwise false.
	 */
	const isFetchingForTheFirstTime = computed<boolean>(() => {
		return isFetching.value && clientPagination.value.page === 1;
	});
	/**
	 * Computed property returns a boolean value indicating whether the properties list is empty.
	 */
	const hasProperties = computed<boolean>(() => properties.value.length > 0);
	/*
	 * Computed property returns a boolean value indicating whether the properties list has less than ten (10) properties.
	 */
	const hasLessThenTenProperties = computed<boolean>(
		() => properties.value.length < 10
	);
	/**
	 * Computed property returns the number of properties that have been filtered in request.
	 */
	const filteredCount = computed<number>(
		() => clientPagination.value.filteredCount
	);

	function syncPageQueryParamWithDisplayPagination() {
		router.replace({
			path: route.path,
			query: {
				...route.query,
				page: displayPagination.value.page,
				sortBy: filters.value.sortBy,
				orderBy: filters.value.orderBy
			}
		});
	}

	/**
	 * Fetches the next page of properties
	 * If more properties are needed, fetches from the server
	 * Otherwise, fetches from the client
	 */
	function nextPage() {
		if (needMoreProperties()) {
			nextServerPage();
			propertiesDataFetcher.execute();
		} else {
			nextClientPage();
			movePropertiesFromCached();
		}

		nextDisplayPage();
		syncPageQueryParamWithDisplayPagination();

		viewMoreBus.emit(page);
	}

	const filterEvents = useEventBus<string>(filtersEventsChangeKey);
	/**
	 * Handles filter changes and updates the URL path accordingly.
	 * Fetches new property data after updating filters.
	 */
	filterEvents.on(async () => {
		updatePathFromFilters(collection);

		if (import.meta.client && config.shouldScrollToTopOnFiltersChange) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}

		await propertiesDataFetcher.execute();
	});

	/**
	 * Resets the states when navigating from imoveis to colecao or from colecao to imoveis
	 * Or moving from imoveis/colecao to another page that is not imoveis/colecao
	 */
	router.afterEach((to, from) => {
		const allowedPages = ['imoveis', 'colecao'];

		const isGoingToAnotherPage = !allowedPages.some(page =>
			to.fullPath.includes(page)
		);

		const isTransitioningBetweenSamePage = allowedPages.some(
			page => from.fullPath.includes(page) && to.fullPath.includes(page)
		);

		const isNavigatingBetweenImoveis =
			from.fullPath.includes('imoveis') && to.fullPath.includes('colecao');
		const isNavigatingBetweenColecao =
			from.fullPath.includes('colecao') && to.fullPath.includes('imoveis');
		const isNavigatingBetweenAllowedPages =
			isNavigatingBetweenImoveis || isNavigatingBetweenColecao;

		if (
			!isTransitioningBetweenSamePage ||
			isGoingToAnotherPage ||
			isNavigatingBetweenAllowedPages
		) {
			resetPagination();
			resetChanges();
			resetFilters();
		}
	});

	function isSortingOnlyFilterChange(newFilters: PropertyFilters) {
		const hasSortingParametersChanged =
			newFilters.sortBy !== filters.value.sortBy ||
			newFilters.orderBy !== filters.value.orderBy;

		if (!hasSortingParametersChanged) return false;

		const currentFiltersExcludingSorting = { ...filters.value };
		const newFiltersExcludingSorting = { ...newFilters };

		delete currentFiltersExcludingSorting.sortBy;
		delete currentFiltersExcludingSorting.orderBy;
		delete newFiltersExcludingSorting.sortBy;
		delete newFiltersExcludingSorting.orderBy;

		return (
			getStringifiedFilters(currentFiltersExcludingSorting) ===
			getStringifiedFilters(newFiltersExcludingSorting)
		);
	}

	/**
	 * Watches route changes and updates filters, properties, and pagination when coming from another page.
	 */
	watch(
		route,
		async function updateFiltersFromRouteChange(newRoute) {
			const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
				newRoute.fullPath,
				filters.value,
				page
			);

			const currentFilterState = getStringifiedFilters();
			const newFilterState = getStringifiedFilters(newFilters);

			/**
			 * Determines whether the user navigated to the current page from a different page or
			 * directly landed on the page due to a route update (e.g., clicking a link within the listing).
			 * This variable represents the following flows:
			 * 1. Entry from another page.
			 * 2. Direct entry to the page but triggered by a route update (e.g., clicking a link within the listing).
			 */
			const hasFilterStateChanged = currentFilterState !== newFilterState;
			const isSortingOnlyChange = isSortingOnlyFilterChange(newFilters);
			const isOnlyPageChange =
				Object.keys(route.query).filter(
					key => key !== 'sortBy' && key !== 'orderBy'
				).length === 1 && 'page' in route.query;

			switch (true) {
				case hasFilterStateChanged && !isSortingOnlyChange:
					resetPagination();
					resetProperties();
					resetChanges();
					updateFiltersFromNewPath(route.fullPath);

					if (import.meta.client && config.shouldScrollToTopOnFiltersChange) {
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}

					await propertiesDataFetcher.execute();
					break;

				case isOnlyPageChange:
					// Keep current filters and sorting
					if (newFilters.sortBy && newFilters.orderBy) {
						updateMultipleFilters(
							{
								sortBy: newFilters.sortBy,
								orderBy: newFilters.orderBy
							},
							false
						);
					}
					break;

				case isSortingOnlyChange:
					updateMultipleFilters(
						{
							sortBy: newFilters.sortBy,
							orderBy: newFilters.orderBy
						},
						true
					);
					break;
				default:
					break;
			}
		},
		{ immediate: true }
	);

	return {
		properties,
		filters,
		filteredCount,
		isFetching,
		isFetchingOnClient,
		isFetchingForTheFirstTime,
		hasProperties,
		hasLessThenTenProperties,
		nextPage,
		updateFiltersManager: updateMultipleFilters,
		updateFiltersManagerDebounced: updateMultipleFiltersDebounced,
		clearFilters
	};
}
